import { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import {
  setInvoiceServiceSmart,
  getFinancialDataService,
  getAllExistingInvoices,
} from "modules/financiera/services/facturaService";

// Columnas requeridas (TODAS son obligatorias)
const REQUIRED_COLUMNS = ["Factura", "NIT", "Empresa", "Valor", "Codigo"];

const REQUIRED_COLUMNS_ALTERNATIVES = {
  Factura: [
    "# Factura",
    "Factura",
    "factura",
    "FACTURA",
    "invoice",
    "Invoice",
    "INVOICE",
    "invoice_number",
    "invoiceNumber",
    "numero_factura",
    "numeroFactura",
    "No Factura",
    "No.",
    "#",
  ],
  NIT: ["nit", "NIT", "Nit", "identificacion", "IDENTIFICACION", "id", "ID"],
  Empresa: [
    "empresa",
    "Empresa",
    "EMPRESA",
    "company",
    "Company",
    "COMPANY",
    "razon_social",
    "RazonSocial",
  ],
  Valor: [
    "valor",
    "Valor",
    "VALOR",
    "amount",
    "Amount",
    "AMOUNT",
    "monto",
    "Monto",
    "MONTO",
    "total",
    "Total",
  ],
  Codigo: [
    "Código",
    "Codigo",
    "codigo",
    "CODIGO",
    "code",
    "Code",
    "CODE",
    "codigo_empresarial",
    "codigoEmpresarial",
    "empresa_codigo",
    "empresaCodigo",
  ],
};

export const useFormFacturas = () => {
  const isMounted = useRef(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados del formulario
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState("");
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [duplicateInDatabase, setDuplicateInDatabase] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ==================== MANEJO DE AUTENTICACIÓN ====================

  const analyzeLocalStorageData = useCallback(() => {
    try {
      const userData = localStorage.getItem("user");

      if (!userData) return { user: null };

      const parsed = JSON.parse(userData);

      let extractedUser = {
        id: null,
        username: null,
        email: null,
        rawData: parsed,
      };

      if (parsed.user?.username) {
        extractedUser.username = parsed.user.username;
        extractedUser.id = parsed.user.id;
        extractedUser.email = parsed.user.email;
      } else if (parsed.username) {
        extractedUser.username = parsed.username;
        extractedUser.id = parsed.id;
        extractedUser.email = parsed.email;
      } else if (parsed.more?.username) {
        extractedUser.username = parsed.more.username;
        extractedUser.id = parsed.more.id;
        extractedUser.email = parsed.more.email;
      }

      if (!extractedUser.username) {
        const searchRecursively = (obj) => {
          if (!obj || typeof obj !== "object") return;

          Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (key.toLowerCase() === "username" && !extractedUser.username) {
              extractedUser.username = value;
            }
            if (key.toLowerCase() === "id" && !extractedUser.id) {
              extractedUser.id = value;
            }
            if (typeof value === "object" && value !== null) {
              searchRecursively(value);
            }
          });
        };
        searchRecursively(parsed);
      }

      return { user: extractedUser };
    } catch (error) {
      console.error("❌ [useFormFacturas] Error analizando datos:", error);
      return { user: null };
    }
  }, []);

  useEffect(() => {
    const { user } = analyzeLocalStorageData();

    if (user) {
      setCurrentUser(user);
    }
    setLoading(false);

    return () => {
      isMounted.current = false;
    };
  }, [analyzeLocalStorageData]);

  // ==================== FUNCIONES DE VALIDACIÓN CON DUPLICADOS ====================

  const normalizeColumnName = (name) => {
    if (!name) return "";
    return name.toString().trim();
  };

  const findColumn = (columns, requiredKey) => {
    const alternatives = REQUIRED_COLUMNS_ALTERNATIVES[requiredKey] || [requiredKey];
    const found = columns.find((col) =>
      alternatives.some((alt) => normalizeColumnName(col) === normalizeColumnName(alt))
    );
    return found || null;
  };

  // Nueva función para validar duplicados dentro del mismo Excel
  const findDuplicatesInExcel = (dataRows, facturaColumnIndex) => {
    const seen = new Map();
    const duplicates = [];

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const facturaValue = row[facturaColumnIndex];

      if (facturaValue && facturaValue.toString().trim() !== "") {
        const facturaKey = facturaValue.toString().trim();

        if (seen.has(facturaKey)) {
          duplicates.push({
            row: i + 2, // +2 porque el índice 0 es el header y sumamos 1
            factura: facturaKey,
            firstOccurrence: seen.get(facturaKey),
          });
        } else {
          seen.set(facturaKey, i + 2);
        }
      }
    }

    return duplicates;
  };

  // Nueva función para validar duplicados contra la base de datos
  const findDuplicatesInDatabase = (dataRows, facturaColumnIndex, existingInvoices) => {
    const duplicates = [];
    const existingSet = new Set(existingInvoices.map((inv) => inv.toString().trim()));

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const facturaValue = row[facturaColumnIndex];

      if (facturaValue && facturaValue.toString().trim() !== "") {
        const facturaKey = facturaValue.toString().trim();

        if (existingSet.has(facturaKey)) {
          duplicates.push({
            row: i + 2,
            factura: facturaKey,
          });
        }
      }
    }

    return duplicates;
  };

  const validateExcelFile = (file, existingInvoices = []) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const errors = [];

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });

          if (!jsonData || jsonData.length === 0) {
            errors.push("El archivo está vacío");
            reject({
              valid: false,
              errors,
              data: null,
              duplicatesInFile: [],
              duplicatesInDatabase: [],
            });
            return;
          }

          const headers = jsonData[0].map((h) => normalizeColumnName(h));

          const missingColumns = [];
          const foundColumns = {};

          // Verificar cada columna requerida
          for (const required of REQUIRED_COLUMNS) {
            const found = findColumn(headers, required);
            if (found) {
              foundColumns[required] = found;
            } else {
              missingColumns.push(required);
            }
          }

          if (missingColumns.length > 0) {
            errors.push(`Columnas requeridas no encontradas: ${missingColumns.join(", ")}`);
            errors.push(`Columnas encontradas en el archivo: ${headers.join(", ")}`);
            errors.push(`Las columnas requeridas son: # Factura, NIT, Empresa, Valor, Código`);
            reject({
              valid: false,
              errors,
              data: null,
              duplicatesInFile: [],
              duplicatesInDatabase: [],
            });
            return;
          }

          if (jsonData.length <= 1) {
            errors.push("El archivo no contiene datos (solo encabezados)");
            reject({
              valid: false,
              errors,
              data: null,
              duplicatesInFile: [],
              duplicatesInDatabase: [],
            });
            return;
          }

          const dataRows = jsonData.slice(1);

          // Obtener el índice de la columna Factura
          const facturaColumnIndex = headers.findIndex((h) => h === foundColumns["Factura"]);

          // Validar duplicados dentro del mismo Excel
          const duplicatesInFile = findDuplicatesInExcel(dataRows, facturaColumnIndex);

          // Validar duplicados contra la base de datos
          const duplicatesInDatabase = findDuplicatesInDatabase(
            dataRows,
            facturaColumnIndex,
            existingInvoices
          );

          // Si hay duplicados, agregar errores
          if (duplicatesInFile.length > 0) {
            errors.push(
              `Se encontraron ${duplicatesInFile.length} factura(s) duplicada(s) dentro del mismo archivo:`
            );
            duplicatesInFile.slice(0, 5).forEach((dup) => {
              errors.push(
                `  - Fila ${dup.row}: Factura "${dup.factura}" (también aparece en fila ${dup.firstOccurrence})`
              );
            });
            if (duplicatesInFile.length > 5) {
              errors.push(`  ... y ${duplicatesInFile.length - 5} duplicados más`);
            }
          }

          if (duplicatesInDatabase.length > 0) {
            errors.push(
              `Se encontraron ${duplicatesInDatabase.length} factura(s) que ya existen en el sistema:`
            );
            duplicatesInDatabase.slice(0, 5).forEach((dup) => {
              errors.push(`  - Fila ${dup.row}: Factura "${dup.factura}" ya está registrada`);
            });
            if (duplicatesInDatabase.length > 5) {
              errors.push(`  ... y ${duplicatesInDatabase.length - 5} facturas más ya existen`);
            }
          }

          // Si hay duplicados, rechazar la validación
          if (duplicatesInFile.length > 0 || duplicatesInDatabase.length > 0) {
            reject({
              valid: false,
              errors,
              data: null,
              duplicatesInFile,
              duplicatesInDatabase,
            });
            return;
          }

          let emptyRows = 0;
          let invalidRows = [];

          for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];

            // Obtener valores de cada columna
            const facturaValue = row[headers.findIndex((h) => h === foundColumns["Factura"])];
            const nitValue = row[headers.findIndex((h) => h === foundColumns["NIT"])];
            const empresaValue = row[headers.findIndex((h) => h === foundColumns["Empresa"])];
            const valorValue = row[headers.findIndex((h) => h === foundColumns["Valor"])];
            const codigoValue = row[headers.findIndex((h) => h === foundColumns["Codigo"])];

            // Verificar si la fila está completamente vacía
            const isEmptyRow =
              (!facturaValue || facturaValue === "") &&
              (!nitValue || nitValue === "") &&
              (!empresaValue || empresaValue === "") &&
              (!valorValue || valorValue === "") &&
              (!codigoValue || codigoValue === "");

            if (isEmptyRow) {
              emptyRows++;
              continue;
            }

            // Validar # Factura
            if (!facturaValue || facturaValue === "") {
              invalidRows.push({
                row: i + 2,
                field: "Factura",
                message: "Número de factura está vacío",
              });
            }

            // Validar NIT
            if (!nitValue || nitValue === "") {
              invalidRows.push({ row: i + 2, field: "NIT", message: "NIT está vacío" });
            }

            // Validar Empresa
            if (!empresaValue || empresaValue === "") {
              invalidRows.push({ row: i + 2, field: "Empresa", message: "Empresa está vacía" });
            }

            // Validar Valor
            if (!valorValue || valorValue === "") {
              invalidRows.push({ row: i + 2, field: "Valor", message: "Valor está vacío" });
            } else if (isNaN(parseFloat(valorValue)) || parseFloat(valorValue) <= 0) {
              invalidRows.push({
                row: i + 2,
                field: "Valor",
                message: "Valor debe ser un número mayor a 0",
              });
            }

            // Validar Código (OBLIGATORIO)
            if (!codigoValue || codigoValue === "") {
              invalidRows.push({
                row: i + 2,
                field: "Código",
                message: "Código está vacío. Este campo es obligatorio",
              });
            }
          }

          if (invalidRows.length > 0) {
            errors.push(`Se encontraron ${invalidRows.length} filas con datos inválidos:`);
            invalidRows.slice(0, 10).forEach((err) => {
              errors.push(`  - Fila ${err.row}: ${err.field} - ${err.message}`);
            });
            if (invalidRows.length > 10) {
              errors.push(`  ... y ${invalidRows.length - 10} filas más`);
            }
            reject({
              valid: false,
              errors,
              data: null,
              duplicatesInFile: [],
              duplicatesInDatabase: [],
            });
            return;
          }

          if (emptyRows > 0) {
            console.warn(`Se omitieron ${emptyRows} filas vacías`);
          }

          const validRowsCount = dataRows.length - emptyRows;

          resolve({
            valid: true,
            errors: [],
            data: {
              headers,
              dataRows,
              foundColumns,
              validRowsCount,
            },
            duplicatesInFile: [],
            duplicatesInDatabase: [],
          });
        } catch (error) {
          console.error("Error al leer el archivo:", error);
          errors.push("No se pudo leer el archivo. Verifique que sea un Excel válido.");
          reject({
            valid: false,
            errors,
            data: null,
            duplicatesInFile: [],
            duplicatesInDatabase: [],
          });
        }
      };

      reader.onerror = () => {
        errors.push("Error al leer el archivo");
        reject({
          valid: false,
          errors,
          data: null,
          duplicatesInFile: [],
          duplicatesInDatabase: [],
        });
      };

      reader.readAsArrayBuffer(file);
    });
  };

  // ==================== FUNCIÓN PARA OBTENER CONTEO DE FACTURAS ====================

  const getCurrentInvoiceCount = useCallback(async () => {
    try {
      const result = await getFinancialDataService();
      if (result.success) {
        return result.total;
      }
      return 0;
    } catch (error) {
      console.error("Error obteniendo conteo de facturas:", error);
      return 0;
    }
  }, []);

  // ==================== MANEJADORES DEL FORMULARIO ====================

  const handleFileChange = async (file, event) => {
    if (file) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (extension !== "xlsx" && extension !== "xls") {
        setSnackbar({
          open: true,
          message: "Solo se permiten archivos .xlsx o .xls",
          severity: "error",
        });
        setSelectedFile(null);
        setValidationErrors([]);
        setDuplicateInFile([]);
        setDuplicateInDatabase([]);
        if (event) event.target.value = "";
        return false;
      }

      setValidating(true);
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);

      try {
        // Obtener facturas existentes antes de validar
        const existingInvoicesResult = await getAllExistingInvoices();
        const existingInvoices = existingInvoicesResult.success
          ? existingInvoicesResult.invoiceNumbers
          : [];

        const validationResult = await validateExcelFile(file, existingInvoices);

        if (validationResult.valid) {
          setSelectedFile(file);
          setValidationErrors([]);
          setDuplicateInFile([]);
          setDuplicateInDatabase([]);
          setSnackbar({
            open: true,
            message: `Archivo válido. Se encontraron ${validationResult.data.validRowsCount} facturas para procesar.`,
            severity: "success",
          });
          return true;
        } else {
          setSelectedFile(null);
          setValidationErrors(validationResult.errors);
          setDuplicateInFile(validationResult.duplicatesInFile || []);
          setDuplicateInDatabase(validationResult.duplicatesInDatabase || []);
          setSnackbar({
            open: true,
            message: `Archivo inválido: ${validationResult.errors[0]}`,
            severity: "error",
          });
          if (event) event.target.value = "";
          return false;
        }
      } catch (validationResult) {
        setSelectedFile(null);
        setValidationErrors(validationResult.errors);
        setDuplicateInFile(validationResult.duplicatesInFile || []);
        setDuplicateInDatabase(validationResult.duplicatesInDatabase || []);
        setSnackbar({
          open: true,
          message: `Archivo inválido: ${validationResult.errors[0]}`,
          severity: "error",
        });
        if (event) event.target.value = "";
        return false;
      } finally {
        setValidating(false);
      }
    }
    return false;
  };

  const handleImportProgress = useCallback((progress, message) => {
    if (isMounted.current) {
      setImportProgress(progress);
      if (message) {
        setImportMessage(message);
      }
      if (progress > 10 && progress < 100 && message && !message.includes("Iniciando")) {
        setSnackbar({
          open: true,
          message: message || `Procesando facturas: ${Math.round(progress)}% completado...`,
          severity: "info",
        });
      }
    }
  }, []);

  const handleImport = async (onImportSuccess, onError) => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Seleccione un archivo válido para importar",
        severity: "error",
      });
      return { success: false, error: "No hay archivo seleccionado" };
    }

    const countBefore = await getCurrentInvoiceCount();

    setImporting(true);
    setImportProgress(0);
    setImportMessage("");
    setShowConfirmModal(true);
    setImportResult(null);

    try {
      const result = await setInvoiceServiceSmart(selectedFile, handleImportProgress);

      if (isMounted.current) {
        if (!result.success || (!result.importedCount && result.success === false)) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const countAfter = await getCurrentInvoiceCount();
          const newInvoicesCount = countAfter - countBefore;

          if (newInvoicesCount > 0) {
            const successResult = {
              success: true,
              message: `El archivo se procesó correctamente. Se importaron ${newInvoicesCount} factura(s).`,
              importedCount: newInvoicesCount,
              verified: true,
            };
            setImportResult(successResult);
            setImportProgress(100);

            if (onImportSuccess) {
              onImportSuccess(successResult);
            }
            setSnackbar({
              open: true,
              message: successResult.message,
              severity: "success",
            });
            return successResult;
          }
        }

        setImportResult(result);
        setImportProgress(100);

        const finalCount = result.importedCount || (result.success && result.data?.processed) || 0;

        if (result.success || finalCount > 0) {
          if (onImportSuccess) {
            onImportSuccess(result);
          }
          const successMessage =
            result.message ||
            `Archivo procesado exitosamente. Se importaron ${finalCount} factura(s).`;
          setSnackbar({
            open: true,
            message: successMessage,
            severity: "success",
          });
        } else {
          if (onError) {
            onError(result.error);
          }
          setSnackbar({
            open: true,
            message: result.error || "Error al procesar el archivo",
            severity: "error",
          });
        }

        return result;
      }
    } catch (error) {
      const countAfter = await getCurrentInvoiceCount();
      const newInvoicesCount = countAfter - countBefore;

      if (newInvoicesCount > 0) {
        const successResult = {
          success: true,
          message: `El archivo se procesó correctamente a pesar del error de conexión. Se importaron ${newInvoicesCount} factura(s).`,
          importedCount: newInvoicesCount,
          verified: true,
        };
        setImportResult(successResult);
        setImportProgress(100);

        if (onImportSuccess) {
          onImportSuccess(successResult);
        }
        setSnackbar({
          open: true,
          message: successResult.message,
          severity: "success",
        });
        return successResult;
      }

      const errorMsg = error.message || "Error al procesar";
      const errorResult = { success: false, error: errorMsg };

      if (isMounted.current) {
        setImportResult(errorResult);
        if (onError) onError(errorMsg);
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: "error",
        });
      }

      return errorResult;
    } finally {
      if (isMounted.current) {
        setImporting(false);
        setTimeout(() => {
          if (isMounted.current) {
            setImportProgress(0);
            setImportMessage("");
          }
        }, 2000);
      }
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    if (importResult?.success) {
      setSelectedFile(null);
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);
      setImportProgress(0);
      setImportMessage("");
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setImportProgress(0);
    setImportMessage("");
    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getCurrentUsername = useCallback(() => {
    return currentUser?.username || "Usuario no identificado";
  }, [currentUser]);

  return {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    currentUser,
    loading,
    importProgress,
    importMessage,
    duplicateInFile,
    duplicateInDatabase,
    getCurrentUsername,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
  };
};

export default useFormFacturas;
